import Component from '@glimmer/component'
import { startOfMonth, endOfMonth, format } from 'date-fns'
import { reportingTitle, reportingToolTip as toolTipConfig } from 'teacher-ui-client/utils/giraffe/highcharts-config'
import { colors } from '@colors'
import BaseHighcharterComponent from 'teacher-ui-client/components/base/highcharter'
import type * as Highcharts from 'highcharts'

interface Args {
  chartMargin?: (number | null)[]
  data: Highcharts.SeriesOptionsType[]
  fromDate: Date
  legend: Highcharts.LegendOptions
  plotLines?: Highcharts.YAxisPlotLinesOptions[]
  toDate: Date
  toolTipFormatter: Highcharts.TooltipFormatterCallbackFunction
  yAxisLabel: string
  yAxisLabelFormatter?: Highcharts.AxisLabelsFormatterCallbackFunction
  yRange?: [number, number]
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

const disabled = { enabled: false }

export class ScatterPlot extends Component<Signature> {
  get chartMargins() {
    if (!this.args.chartMargin) return { margin: undefined }

    const [marginTop, marginRight, marginBottom, marginLeft] = this.args.chartMargin

    return {
      marginTop: marginTop ?? undefined,
      marginRight: marginRight ?? undefined,
      marginBottom: marginBottom ?? undefined,
      marginLeft: marginLeft ?? undefined,
    }
  }

  plotBackgroundColor = colors.dustyBlack50

  yAxisGridLineColor = colors.dustyBlack100

  chartSpacing = [10, 10, 10, 10]

  get chartMargin() {
    return this.args.chartMargin
  }

  get chartOptions(): Highcharts.Options {
    return {
      chart: {
        type: 'scatter',
        plotBackgroundColor: this.plotBackgroundColor,
        backgroundColor: 'transparent',
        spacing: this.chartSpacing,
        ...this.chartMargins,
      },
      credits: disabled,
      yAxis: this.yAxis,
      xAxis: this.xAxis,
      plotOptions: this.plotOptions,
      title: { text: '' },
      tooltip: this.toolTip,
      legend: this.args.legend ?? disabled,
      time: {
        timezone: undefined, // do not use UTC
      },
    }
  }

  get highchartOptions() {
    return { ...this.chartOptions, series: this.args.data }
  }

  get plotLines() {
    return this.args.plotLines ?? []
  }

  get plotOptions() {
    return {
      scatter: {
        marker: {
          symbol: 'circle',
          radius: 5,
        },
      },
    }
  }

  get toolTip() {
    const formatter = this.args.toolTipFormatter

    return toolTipConfig('y', '', { formatter, backgroundColor: colors.neutral400 })
  }

  get xAxisMin() {
    return startOfMonth(this.args.fromDate).valueOf()
  }

  get xAxisMax() {
    return endOfMonth(this.args.toDate).valueOf()
  }

  get xAxis(): Highcharts.XAxisOptions {
    return {
      title: reportingTitle(''),
      visible: true,
      type: 'datetime',
      endOnTick: true,
      showLastLabel: false,
      units: [['month', [1]]],
      min: this.xAxisMin,
      max: this.xAxisMax,
      lineColor: colors.dustyBlack200,
      tickColor: colors.dustyBlack200,
      labels: {
        formatter(this: Highcharts.AxisLabelsFormatterContextObject) {
          return typeof this.value === 'number' ? format(new Date(this.value), 'MMM') : ''
        },
      },
    }
  }

  get yAxis(): Highcharts.YAxisOptions {
    const [min, max] = this.yRange
    const { yAxisLabelFormatter } = this.args
    const gridLineColor = this.yAxisGridLineColor
    const allowDecimals = false

    const yAxis: Highcharts.YAxisOptions = {
      title: reportingTitle(this.args.yAxisLabel),
      visible: true,
      min,
      max,
      plotLines: this.plotLines,
      plotBands: [],
      allowDecimals,
      gridLineColor,
    }

    if (min) {
      yAxis.startOnTick = false
    }

    if (max) {
      yAxis.endOnTick = false
    }

    if (yAxisLabelFormatter) {
      yAxis.labels = {
        formatter: yAxisLabelFormatter,
      }
    }

    return yAxis
  }

  get yRange() {
    return this.args.yRange ?? []
  }

  <template><BaseHighcharterComponent class="h-full" @options={{this.highchartOptions}} ...attributes /></template>
}

export default ScatterPlot
